import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { minWidth } from '../../css/breakpoints';
import { panelIds } from './types';
import type { OpenPanel, Panel } from './types';

export default function useMobilePanel() {
  const [panel, setPanel] = useState<Panel>('closed');
  const headerRef = useRef<HTMLDivElement>(null);

  const isDesktop = useMediaQuery(minWidth('lg'));

  useEffect(() => {
    if (isDesktop) setPanel('closed');
  }, [isDesktop]);

  function focusToggle(target: OpenPanel) {
    headerRef.current
      ?.querySelector<HTMLElement>(`[aria-controls="${panelIds[target]}"]`)
      ?.focus();
  }

  function close() {
    setPanel('closed');
  }

  function closeSearch() {
    setPanel('closed');
    focusToggle('search');
  }

  function togglePanel(nextPanel: OpenPanel) {
    if (panel !== nextPanel) setPanel(nextPanel);
    else if (nextPanel === 'search') closeSearch();
    else close();
  }

  // Radix handles Escape inside the menu dialog.
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Escape' || panel !== 'search') return;
    event.preventDefault();
    closeSearch();
  }

  // Leave focus alone when the dialog closed to open search.
  function onMenuClosed() {
    if (panel !== 'closed') return;
    focusToggle('menu');
  }

  return {
    panel,
    headerRef,
    togglePanel,
    close,
    onKeyDown,
    onMenuClosed,
  };
}
